import Vue from "vue";
import VueRouter from "vue-router";
import beforeEach from "./beforeEach";

Vue.use(VueRouter);

// meta.permission: regra de acesso da tela (ver shared/permissions.js). Telas
// de detalhe abrem tambem a criacao ("/alunos/novo"), que pede outra acao.
const byId = (module) => (to) =>
  to.params.id === "novo" ? `${module}.create` : `${module}.view`;
const materialPermission = (to) =>
  to.params.idMaterial === "novo" ? "course.update" : "course.view";

const routes = [
  {
    path: "/sign-in",
    name: "signIn",
    component: () => import("@/views/Login.vue"),
  },
  {
    path: "/cockpit",
    name: "Cockpit",
    component: () => import("@/views/home/Home.vue"),
    meta: { requiredAuth: true },
    children: [
      // Card 86akputj7: o Dashboard Geral de Relatorios substitui o Cockpit
      // como tela inicial do administrativo. Rota filha com "/" resolve para o
      // caminho do pai: a tela inicial continua em /cockpit.
      {
        path: "/",
        name: "Home",
        meta: { requiredAuth: true, permission: "dashboard.view" },
        component: () => import("@/views/home/dashboard/Dashboard.vue"),
        children: [],
      },

      //usuarios
      {
        path: "/usuarios",
        name: "Usuários",
        meta: { requiredAuth: true, permission: { superAdmin: true } },
        component: () => import("@/views/home/users/Users.vue"),
        children: [],
      },

      {
        // Alunos
        path: "/alunos",
        name: "Alunos",
        meta: { requiredAuth: true, permission: "customers.list" },
        component: () => import("@/views/home/customers/Customers.vue"),
        children: [],
      },
      {
        // Recorte dos alunos que estudam sem professor (fluxo solo): a mesma
        // base de /alunos, mas com as pendencias de tutor e mensalidade a vista.
        path: "/alunos-sem-professor",
        name: "Alunos sem professor",
        meta: { requiredAuth: true, permission: "customers.list" },
        component: () =>
          import("@/views/home/customers/CustomersWithoutTeacher.vue"),
        children: [],
      },
      {
        path: "/alunos/:id",
        meta: { requiredAuth: true, permission: byId("customers") },
        name: "Detalhes aluno",
        component: () =>
          import("@/views/home/customers/form/CustomersForm.vue"),
        children: [],
      },

      // Professores
      {
        path: "/professores",
        name: "Professores",
        meta: { requiredAuth: true, permission: "partners.list" },
        component: () => import("@/views/home/partners/Partners.vue"),
        children: [],
      },
      {
        path: "/professores/:id",
        name: "Detalhes professor",
        meta: { requiredAuth: true, permission: byId("partners") },
        component: () => import("@/views/home/partners/form/PartnersForm.vue"),
      },
      {
        path: "/agenda",
        name: "Agenda",
        meta: { requiredAuth: true, permission: "agenda.list" },
        component: () => import("@/views/home/agenda/Agenda.vue"),
        children: [],
      },
      // Financeiro
      {
        path: "/pagamentos",
        name: "Financeiro",
        meta: { requiredAuth: true, permission: "finance.list" },
        component: () => import("@/views/home/finance/Finance.vue"),
        children: [],
      },
      {
        path: "/turmas",
        name: "Turmas",
        meta: { requiredAuth: true, permission: "groups.list" },
        component: () => import("@/views/home/groups/Groups.vue"),
        children: [],
      },
      // Relatorio de acompanhamento de turmas e alunos (card 86akputj7): virou
      // uma aba do dashboard. O endereco antigo continua levando ate ela.
      {
        path: "/relatorios",
        name: "Relatórios",
        meta: { requiredAuth: true },
        redirect: { path: "/cockpit", query: { aba: "turmas" } },
      },
      // Monitoramento: auditoria dos conteudos que professores compartilham
      // com alunos por anotacao.
      {
        path: "/monitoramento",
        name: "Monitoramento",
        meta: { requiredAuth: true, permission: "monitoring.list" },
        component: () => import("@/views/home/monitoring/Monitoring.vue"),
        children: [],
      },
      // Central de Comunicacao (card 86akj8ptp). O menu ja apontava para
      // "/comunicação", que nao existia como rota -- era um link morto.
      {
        path: "/comunicacao",
        name: "Comunicação",
        meta: { requiredAuth: true, permission: "communications.list" },
        component: () =>
          import("@/views/home/communications/Communications.vue"),
        children: [],
      },
      // Card 17trt6jbffe: Mensagens (espelho das conversas e atendimento do
      // suporte). Separada de "Comunicação", que segue sendo a dos comunicados.
      {
        path: "/mensagens",
        name: "Mensagens",
        meta: { requiredAuth: true, permission: "messages.list" },
        component: () => import("@/views/home/messages/Messages.vue"),
        children: [],
      },
      // Formulários de pesquisa (card 86akqxu0h): construtor, envio pela
      // Central de Comunicação e respostas.
      {
        path: "/formularios",
        name: "Formulários",
        meta: { requiredAuth: true, permission: "surveys.list" },
        component: () => import("@/views/home/surveys/Surveys.vue"),
        children: [],
      },
      {
        path: "/formularios/:id",
        name: "Formulário",
        meta: { requiredAuth: true, permission: "surveys.view" },
        component: () => import("@/views/home/surveys/SurveyDetail.vue"),
      },
      // Planos de estudo (card 86akj8q08): modelos, envio a alunos autônomos e
      // acompanhamento.
      {
        path: "/planos-de-estudo",
        name: "Planos de estudo",
        meta: {
          requiredAuth: true,
          permission: ["studyPlans.list", "studyPlans.grade"],
        },
        component: () => import("@/views/home/studyPlans/StudyPlans.vue"),
      },
      // Certificados (card 17trt6jfkw8): emitidos pelos alunos e visualização de
      // modelo (abas por permissão).
      {
        path: "/certificados",
        name: "Certificados",
        meta: {
          requiredAuth: true,
          permission: ["certificates.list", "certificates.preview"],
        },
        component: () => import("@/views/home/certificates/Certificates.vue"),
      },
      // Pitch Coins (card 17trt6jd8uu): regras, campanhas, lancamentos,
      // extratos, alertas e importacao de saldos (abas por permissao).
      {
        path: "/pitch-coins",
        name: "Pitch Coins",
        meta: {
          requiredAuth: true,
          permission: ["coins.view", "coins.adjust"],
        },
        component: () => import("@/views/home/coins/Coins.vue"),
      },
      // Exercícios (card 17trt6jcyfb): catálogo importado do app antigo,
      // conferência dos vínculos automáticos com as aulas.
      {
        path: "/exercicios",
        name: "Exercícios",
        meta: { requiredAuth: true, permission: "exercises.list" },
        component: () => import("@/views/home/exercises/ExerciseSets.vue"),
      },
      {
        path: "/exercicios/conferencia",
        name: "Conferência de exercícios",
        meta: { requiredAuth: true, permission: "exercises.update" },
        component: () =>
          import("@/views/home/exercises/ExerciseLinkReview.vue"),
      },
      {
        path: "/exercicios/:id",
        name: "Questionário",
        meta: { requiredAuth: true, permission: "exercises.view" },
        component: () => import("@/views/home/exercises/ExerciseSetDetail.vue"),
      },
      // Curso
      {
        path: "/curso",
        name: "Curso",
        meta: { requiredAuth: true, permission: "course.list" },
        component: () => import("@/views/home/course/Course.vue"),
        children: [],
      },
      {
        path: "/curso/:id",
        name: "Detalhes curso",
        meta: { requiredAuth: true, permission: byId("course") },
        component: () => import("@/views/home/course/children/CourseForm.vue"),
      },
      {
        path: "/curso/:id/material/:idMaterial",
        name: "Material do curso",
        meta: { requiredAuth: true, permission: materialPermission },
        component: () =>
          import("@/views/home/course/children/classes/ClassesEditForm.vue"),
      },
      // Avaliações: provas aplicadas aos alunos. A tela ainda não tem
      // conteúdo; saiu do menu em 15/10/2026 a pedido do Lucas. A rota e a
      // permissão "evaluations" ficam para quando a feature existir.
      {
        path: "/avaliacoes",
        name: "Avaliações",
        meta: { requiredAuth: true, permission: "evaluations.list" },
        component: () => import("@/views/home/evaluations/Evaluations.vue"),
      },
      // Perfis e permissoes: so o Administrador cria e edita.
      {
        path: "/perfis",
        name: "Perfis e permissões",
        meta: { requiredAuth: true, permission: { superAdmin: true } },
        component: () => import("@/views/home/profiles/Profiles.vue"),
      },
      // Qualquer admin logado: o proprio perfil e o que ele libera.
      {
        path: "/meu-perfil",
        name: "Meu perfil",
        meta: { requiredAuth: true },
        component: () => import("@/views/home/profiles/MyProfile.vue"),
      },
      // Destino de quem nao tem nenhuma tela liberada (ex.: Estagiario recem
      // criado).
      {
        path: "/sem-acesso",
        name: "Sem acesso",
        meta: { requiredAuth: true },
        component: () => import("@/views/home/profiles/NoAccess.vue"),
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(beforeEach);

export default router;
